import { useRollbar } from '@rollbar/react'
import toast from 'react-hot-toast'
import { LocaleEnum } from '@systemeio/ui-frontend'
import { sanitize } from 'isomorphic-dompurify'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'
import { PrimaryButton, SecondaryButton } from 'shared/components/button'
import { LikeButton } from 'shared/components/like/like-button'
import useUser, { isDomainOwner, isWsAdmin, isWsAssistant } from 'shared/hooks/use-user'
import CommentOutlinedIcon from 'shared/icons/comment-outlined-icon'
import { timeSince } from 'shared/utils/date-time/time-since'
import { AudioPlayerAttachement } from 'modules/attachments/components/audio-player'
import { CommentsList } from 'modules/comments/components'
import { CommentsTypeEnum } from 'modules/comments/types'
import { TextWithMentions } from 'modules/community/components/editor/utils/deserealizeMentionToHTML'
import { PostAuthorInfo } from 'modules/community/components/post-announce/author-info'
import { PostActionsMenu } from 'modules/community/components/post/components/post-actions-menu'
import { PostAttachmentsViewer } from 'modules/community/components/post/components/post-attachments-viewer'
import { PostStatusEnum } from 'modules/community/enums/post-status-enum'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'
import { AttachmentTypeEnum } from 'modules/community/types/post-attachment'
import { PostInterface } from 'modules/community/types/post-interface'
import { CommentsHighlightContext } from '../types'

export const PostView = ({
  post,
  handleApprovePost,
  handleDecliePost,
  handleDeletePost,
  handleDeclinePostAndBanUser,
  deletePostAndBanUser,
  handleLikeClick,
  isLoading,
  commentsHighlightContext,
  targetCommentId,
}: {
  post: PostInterface
  handleApprovePost?: () => void
  handleDecliePost?: () => void
  handleDeletePost?: () => Promise<void>
  handleLikeClick?: () => void
  handleDeclinePostAndBanUser?: (removeRecentActivitiesChecked: boolean) => Promise<void>
  deletePostAndBanUser?: (removeRecentActivitiesChecked: boolean) => Promise<void>
  isLoading: boolean
  commentsHighlightContext?: CommentsHighlightContext
  targetCommentId?: number
}) => {
  const rollbar = useRollbar()
  const { t } = useTranslation()
  const { user, loading: isUserLoading } = useUser()
  const { isUserMember } = useIsUserMember()
  const router = useRouter()
  const { communityPath } = router.query as { communityPath: string }

  const canManagePostStatus =
    post.status !== PostStatusEnum.Approved &&
    user &&
    (isWsAdmin(user) || isDomainOwner(user) || isWsAssistant(user))

  if (post.status !== PostStatusEnum.Pending && !post.publishedAt) {
    rollbar.error('Post view has no publishedAt', { post })
    return null
  }

  return (
    <>
      <div
        className={twMerge(
          'mx-4 pt-6 font-avertaPE sm:mx-6 md:mx-8',
          post.status !== PostStatusEnum.Approved && 'pb-6',
        )}
      >
        <div className="mb-5 flex items-center gap-2">
          <PostAuthorInfo post={post} />

          {canManagePostStatus && handleApprovePost && handleDecliePost && (
            <div className="ml-auto flex gap-3 max-sm:hidden">
              <PrimaryButton onClick={handleApprovePost} isLoading={isUserLoading || isLoading}>
                {t('community.moderation.posts.approve_button.title')}
              </PrimaryButton>
              <SecondaryButton
                className="disabled:border-text-secondary disabled:text-text-secondary"
                onClick={handleDecliePost}
                disabled={post.status === PostStatusEnum.Declined}
                isLoading={isUserLoading || isLoading}
              >
                {t('community.moderation.posts.decline_button.title')}
              </SecondaryButton>
            </div>
          )}
          <PostActionsMenu
            deletePostAndBanUser={
              deletePostAndBanUser
                ? removeRecentActivitiesChecked =>
                    deletePostAndBanUser(removeRecentActivitiesChecked)
                : undefined
            }
            post={post}
            deletePost={handleDeletePost}
            declinePostAndBanUser={handleDeclinePostAndBanUser}
          />
        </div>
        {canManagePostStatus && (
          <div className="mb-2 ml-auto flex gap-3 sm:hidden">
            <PrimaryButton onClick={handleApprovePost} isLoading={isUserLoading || isLoading}>
              {t('community.moderation.posts.approve_button.title')}
            </PrimaryButton>
            <SecondaryButton
              className="disabled:border-text-secondary disabled:text-text-secondary"
              onClick={handleDecliePost}
              disabled={post.status === PostStatusEnum.Declined}
              isLoading={isUserLoading || isLoading}
            >
              {t('community.moderation.posts.decline_button.title')}
            </SecondaryButton>
          </div>
        )}
        <h2
          className="community-post-title break-all pb-1 sm:pb-4"
          dangerouslySetInnerHTML={{
            __html: sanitize(post.title),
          }}
        />
        <TextWithMentions
          className="break-words text-text-primary/90"
          text={post.content}
          mentions={post.mentions}
        />
        {!!post?.attachments?.length &&
          post.attachments.map(
            attachment =>
              attachment.type === AttachmentTypeEnum.Audio && (
                <AudioPlayerAttachement
                  attachment={attachment}
                  height="50px"
                  width="100%"
                  className="mt-3"
                  key={attachment.dataFileId}
                />
              ),
          )}

        <PostAttachmentsViewer attachments={post.attachments || []} />

        {post.status === PostStatusEnum.Approved && !!handleLikeClick && (
          <div className="mt-[22px] flex items-center gap-1">
            <LikeButton
              isLiked={post.isLiked}
              likesCount={post.likesCount}
              handleLikeClick={handleLikeClick}
              postId={post.id}
            />
            <div className="flex items-center gap-2 rounded-md px-1.5 py-2">
              <CommentOutlinedIcon className="size-5 text-text-secondary transition-colors group-hover:text-brand-primary" />
              {post?.commentsCount || ''}
            </div>
          </div>
        )}
      </div>
      {post.status === PostStatusEnum.Approved && (
        <div id="Comments" className="px-4 font-avertaPE sm:px-6 md:px-8">
          <div className="mb-3 mt-4 text-xl font-semibold text-text-primary md:mb-5 md:mt-6">
            {t('post_details.comments')}
          </div>
          {user && (
            <CommentsList
              commentsType={CommentsTypeEnum.Community}
              onError={() => toast.error(t('comments.error.error-loading-comments'))}
              readOnly={!isUserMember}
              community={{
                postId: post.id,
                communityPath: String(communityPath),
              }}
              commentsHighlightContext={commentsHighlightContext}
              targetCommentId={targetCommentId}
            />
          )}
        </div>
      )}
    </>
  )
}
